import React, { Suspense } from 'react'
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { IdiomaProvider } from './context/IdiomaContext'
import { TemaProvider } from './context/TemaContext'
import { useAuthStore } from './store/useAuthStore'
import { useWorkspaceStore } from './store/useWorkspaceStore'
import { useUserQuery } from './hooks/useAuthActions'
import { useEffect } from 'react'
import { QueryProvider, queryClient } from './providers/QueryProvider'
import api from './api'
import Sidebar from './components/Sidebar'
import Asistente from './components/Asistente'
import NotificacionesManager from './components/NotificacionesManager'
import CommandPalette from './components/CommandPalette'
import ApiFeedback from './components/ApiFeedback'

// Lazy Loading de páginas para mejorar rendimiento y code splitting
const Dashboard = React.lazy(() => import('./modules/dashboard/Dashboard'))
const Tareas = React.lazy(() => import('./modules/tareas/Tareas'))
const Calendario = React.lazy(() => import('./pages/Calendario'))
const Planificador = React.lazy(() => import('./pages/Planificador'))
const Habitos = React.lazy(() => import('./modules/habitos/Habitos'))
const Notas = React.lazy(() => import('./modules/notas/Notas'))
const Finanzas = React.lazy(() => import('./modules/finanzas/Finanzas'))
const Login = React.lazy(() => import('./pages/Login'))
const Gimnasio = React.lazy(() => import('./modules/gimnasio/Gimnasio'))
const Calorias = React.lazy(() => import('./modules/calorias/Calorias'))
const Logros = React.lazy(() => import('./pages/Logros'))
const Analytics = React.lazy(() => import('./pages/Analytics'))
const Configuraciones = React.lazy(() => import('./pages/Configuraciones'))
const VerifyEmail = React.lazy(() => import('./pages/VerifyEmail'))
const AdminDashboard = React.lazy(() => import('./pages/AdminDashboard'))
const Legal = React.lazy(() => import('./pages/Legal'))

const SuspenseFallback = () => (
  <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
    <div className="animate-pulse flex flex-col items-center">
      <div className="h-8 w-32 bg-indigo-200 dark:bg-indigo-900 rounded mb-4"></div>
      <p className="text-indigo-600 dark:text-indigo-400 font-semibold text-lg">Cargando Módulo...</p>
    </div>
  </div>
)

function AppContent() {
  const { isAuthenticated, isLoading, setLoading, token } = useAuthStore()

  // Fetch user if token exists
  useUserQuery()

  const { fetchWorkspaces, activeWorkspaceId } = useWorkspaceStore()

  useEffect(() => {
    if (!token) {
      setLoading(false)
    } else {
      fetchWorkspaces()
    }
  }, [token, setLoading, fetchWorkspaces])

  useEffect(() => {
    if (!token || activeWorkspaceId === 'default') return
    let cancelled = false
    const syncCalendar = async () => {
      try {
        const status = await api.googleStatus()
        if (!cancelled && status.connected) {
          await api.syncGoogle()
          if (!cancelled) queryClient.invalidateQueries({ queryKey: ['tareas'] })
        }
      } catch {
        // La sincronización manual sigue disponible si Google requiere autorización.
      }
    }
    syncCalendar()
    return () => { cancelled = true }
  }, [token, activeWorkspaceId])

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <p className="text-indigo-600 font-semibold text-lg animate-pulse">Iniciando DayFlow...</p>
      </div>
    )
  }

  if (!isAuthenticated) return (
    <BrowserRouter>
      <Suspense fallback={<SuspenseFallback />}>
        <Routes>
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/privacidad" element={<Legal />} />
          <Route path="/terminos" element={<Legal />} />
          <Route path="*" element={<Login />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  )

  return (
    <BrowserRouter>
          <CommandPalette />
          <ApiFeedback />
          <NotificacionesManager />
          <div className="flex min-h-screen bg-gray-50 dark:bg-gray-950 overflow-x-hidden relative">
            {/* Animated Background Orbs for Glassmorphism effect */}
            <div className="fixed top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-indigo-500/20 blur-[120px] mix-blend-multiply dark:mix-blend-screen pointer-events-none animate-blob" />
            <div className="fixed top-[20%] right-[-10%] w-[35%] h-[35%] rounded-full bg-purple-500/20 blur-[120px] mix-blend-multiply dark:mix-blend-screen pointer-events-none animate-blob animation-delay-2000" />
            <div className="fixed bottom-[-10%] left-[20%] w-[45%] h-[45%] rounded-full bg-pink-500/20 blur-[120px] mix-blend-multiply dark:mix-blend-screen pointer-events-none animate-blob animation-delay-4000" />
            
            <Sidebar />
            <main className="flex-1 p-4 md:p-8 pt-16 md:pt-8 min-w-0 w-full relative z-10">
              <Suspense fallback={<SuspenseFallback />}>
                <Routes>
                  <Route path="/"           element={<Dashboard />} />
                  <Route path="/tareas"     element={<Tareas />} />
                  <Route path="/calendario" element={<Calendario />} />
                  <Route path="/planificador" element={<Planificador />} />
                  <Route path="/habitos"    element={<Habitos />} />
                  <Route path="/notas"      element={<Notas />} />
                  <Route path="/finanzas"   element={<Finanzas />} />
                  <Route path="/gimnasio"   element={<Gimnasio />} />
                  <Route path="/calorias"   element={<Calorias />} />
                  <Route path="/logros"     element={<Logros />} />
                  <Route path="/analytics"  element={<Analytics />} />
                  <Route path="/configuraciones" element={<Configuraciones />} />
                  <Route path="/admin"      element={<AdminDashboard />} />
                  <Route path="/privacidad" element={<Legal />} />
                  <Route path="/terminos" element={<Legal />} />
                </Routes>
              </Suspense>
            </main>
            <Asistente />
          </div>
        </BrowserRouter>
  )
}

function App() {
  return (
    <QueryProvider>
      <IdiomaProvider>
        <TemaProvider>
          <AppContent />
        </TemaProvider>
      </IdiomaProvider>
    </QueryProvider>
  )
}

export default App
